import { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import Wrapper from "../../helpers/Wrapper";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense("AM5C258896422");

const WaterFallChart = ({
    chartId,
    chartClass,
    chartData,
    chartLabels,
    height = "100%",
}) => {
    useLayoutEffect(() => {
        const root = am5.Root.new(chartId);
        root.numberFormatter.setAll({
            numberFormat: "#.##a",
            bigNumberPrefixes: [
                { number: 1e3, suffix: "K" },
                { number: 1e6, suffix: "M" },
                { number: 1e9, suffix: "B" },
            ],
            smallNumberPrefixes: [],
        });
        // Set themes
        root.setThemes([am5themes_Animated.new(root)]);
        root.interfaceColors.set("grid", am5.color(0xffffff));

        // Create chart
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                paddingRight: 0,
                paddingTop: 10,
                paddingBottom: 0,
                paddingLeft: 0,
                maxTooltipDistance: -1,
            })
        );

        // Create X-axis
        const xRenderer = am5xy.AxisRendererX.new(root, {
            minGridDistance: 30,
            minorGridEnabled: false,
            strokeOpacity: 1,  // Make axis line visible
            stroke: am5.color(0x707070)
        });

        const xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                maxDeviation: 0,
                categoryField: "category",
                renderer: xRenderer,
            })
        );

        // Configure axis elements
        xRenderer.grid.template.setAll({
            visible: false
        });

        // Configure X-axis labels
        xRenderer.labels.template.setAll({
            // visible: true,
            // fontSize: 12,
            // maxWidth: 80,
            // oversizedBehavior: "truncate",
            // textAlign: "center",
            // ellipsis: "...",
            // paddingTop: 8,
            // paddingBottom: 8
            visible: true,
            fontFamily: "Ubuntu", // Font family
            fontWeight: "400",    // Font weight
            fontSize: 12,         // Font size
            fill: am5.color(0x757575), // Text color
            maxWidth: 80,
            oversizedBehavior: "truncate",
            textAlign: "center",
            ellipsis: "...",
            paddingTop: 8,
            paddingBottom: 8
        });

        // Set the axis line properties directly on the renderer
        xRenderer.setAll({
            visible: true,
            strokeOpacity: 1,
            stroke: am5.color(0x707070)  // Black color for the axis line
        });

        // Create Y-axis
        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                maxDeviation: 0,
                min: 0,
                renderer: am5xy.AxisRendererY.new(root, {
                    stroke: am5.color(0x707070),
                    strokeWidth: 1,
                    strokeOpacity: 1,
                    // strokeOpacity: 1,
                    grid: {
                        visible: false
                    }
                }),
            })
        );

        yAxis.get("renderer").grid.template.setAll({
            visible: false
        });

        yAxis.get("renderer").labels.template.setAll({
            // fill: am5.color(0x757575), // Red color
            // fontSize: 14,
            fontFamily: "Ubuntu", // Font family
            fontWeight: "400",    // Font weight
            fontSize: 12,         // Font size
            fill: am5.color(0x757575),
        });


        // Add cursor
        const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
            xAxis: xAxis,
            yAxis: yAxis
        }));

        // ---- Custom Tooltip Implementation ----
        // Create a styled tooltip object, but do not set labelText here (set via adapter!)
        const styledTooltip = am5.Tooltip.new(root, {
            pointerOrientation: "horizontal",
            getFillFromSprite: false,
            getStrokeFromSprite: false,
            autoTextColor: false
        });
        styledTooltip.label.setAll({
            fill: am5.color(0xffffff),
            fontSize: 12,
            fontFamily: "Ubuntu",
        });
        styledTooltip.get("background").setAll({
            fill: am5.color(0x213345),
            cornerRadius: 5,
            stroke: am5.color(0x213345)
        });


        // Add custom adapter for tooltip text
        // styledTooltip.label.adapters.add("text", (text, target) => {
        //     // Try to get the hovered data item (category/bar)
        //     let dataItem = null;
        //     // If triggered by a column
        //     if (target.dataItem) {
        //         dataItem = target.dataItem;
        //     } else if (target._dataItem) {
        //         dataItem = target._dataItem;
        //     }
        //     if (!dataItem) return "";

        //     const ctx = dataItem.dataContext || {};

        //     // Display the category and value, like the main bar tooltip
        //     // You can expand this to show more rows or custom rows if needed
        //     // (like the multi-value tooltips in your other charts)
        //     let tooltipText = `[bold]${ctx.category}[/]\n`;

        //     // Show bar value (positive/negative), always with comma separator
        //     // tooltipText += `[font-size:12px][#07C180]●[/] Value: [bold]${ctx.displayValue?.toLocaleString() ?? ""}[/]`;
        //     // tooltipText += `[font-size:12px][#07C180]●[/] Value: [bold]${ctx.displayValue?.toLocaleString() ?? ""}[/]`;


        //     return tooltipText;
        // });

        // Create column series
        const series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "value",
                openValueYField: "open",
                categoryXField: "category",
                // tooltip: styledTooltip
            })
        );

        series.columns.template.setAll({
            templateField: "columnConfig",
            strokeOpacity: 0
        });
        series.columns.template.set("tooltip", styledTooltip);
        series.columns.template.adapters.add("tooltipText", (text, target) => {
            const ctx = target.dataItem?.dataContext;
            if (!ctx) return "";
            return `[bold]${ctx.category}: [font-size:12px][bold] ${formatNumberForTooltip(ctx.displayValue, ctx.category)}[/]`;
        });

        // Create step series
        const stepSeries = chart.series.push(
            am5xy.StepLineSeries.new(root, {
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "stepValue",
                categoryXField: "category",
                noRisers: true,
                locationX: 0.65,
                stroke: root.interfaceColors.get("alternativeBackground")
            })
        );


        stepSeries.strokes.template.setAll({
            strokeDasharray: [3, 3]
        });

        const generateDynamicData = (data, labels) => {
            const dynamicData = [];
            let cumulativeValue = 0;

            labels.forEach((label, index) => {
                const value = data[index];
                const isFirst = index === 0;
                const isLast = index === labels.length - 1;

                let item;
                if (isLast) {
                    // For "This Year", use the value directly
                    item = {
                        category: label,
                        value: value,
                        open: 0,
                        stepValue: value,
                        columnConfig: {
                            fill: am5.color("#11A1FD"),
                            tooltip: styledTooltip
                        },
                        displayValue: value
                    };
                } else {
                    item = {
                        category: label,
                        value: cumulativeValue + value,
                        open: isFirst ? 0 : cumulativeValue,
                        stepValue: cumulativeValue + value,
                        columnConfig: {
                            fill: isFirst
                                ? am5.color("#085DB6")
                                : value > 0
                                    ? am5.color("#07C180")
                                    : am5.color(0xE96E7A),
                            tooltip: styledTooltip
                        },
                        displayValue: Math.abs(value)
                    };
                    cumulativeValue += value;
                }

                dynamicData.push(item);
            });

            return dynamicData;
        };

        // Generate dynamic data
        const dynamicData = generateDynamicData(chartData, chartLabels);

        // Set data to axes and series
        xAxis.data.setAll(dynamicData);
        series.data.setAll(dynamicData);
        if (dynamicData.length > 1) {
            stepSeries.data.setAll(dynamicData.slice(0, -1));
        }

        // Animate chart
        series.appear(1000);
        chart.appear(1000, 100);

        // Cleanup on unmount
        return () => {
            root.dispose();
        };
    }, [chartId, chartData, chartLabels]);

    return (
        <Wrapper>
            <div id={chartId} className={chartClass} style={{
                height: "340px",
                minHeight: "340px",
                background: "#fff",
                borderRadius: "12px"
            }}></div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 24,
                marginTop: 24
            }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: '#07C180' // Growth color
                    }} />
                    <span style={{
                        fontFamily: 'Ubuntu, sans-serif',
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: '100%',
                        letterSpacing: 0,
                        color: '#213345'
                    }}>
                        Growth
                    </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: '#E96E7A' // Degrowth color
                    }} />
                    <span style={{
                        fontFamily: 'Ubuntu, sans-serif',
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: '100%',
                        letterSpacing: 0,
                        color: '#213345'
                    }}>
                        Degrowth
                    </span>
                </span>
            </div>
        </Wrapper>
    );
};

WaterFallChart.propTypes = {
    chartId: PropTypes.string.isRequired,
    chartClass: PropTypes.string,
    chartData: PropTypes.arrayOf(PropTypes.number).isRequired,
    chartLabels: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default WaterFallChart;